/**
 * Schémas Zod — Public intake POST (Chunk 9.N.1.B2).
 *
 * Surface 9.N.1.B2 (publique, no-auth) :
 *   - POST /v1/public/intake/:token (multipart/form-data)
 *
 * Les fichiers binaires arrivent via multipart, parsés par Hono
 * `c.req.parseBody({ all: true })`. Ces schémas couvrent UNIQUEMENT les
 * champs JSON du payload (consent, comment, email, honeypot). Les fichiers
 * sont validés séparément par `apps/api/src/lib/multipart-validator.ts` +
 * `apps/api/src/lib/magic-bytes.ts` côté API.
 *
 * Loi 25 — cadrage F2 §3.11 :
 *   - consent.accepted obligatoire `true` (pré-cochée NON côté UI).
 *   - consent.timestamp ISO 8601, persisté dans `intake_event.payload.consent`.
 *
 * Honeypot — cadrage §3.4 :
 *   - field `website` : si non-vide → reject silencieux (anti-leak unique
 *     `PUBLIC_INTAKE_LINK_UNAVAILABLE` côté handler).
 */

import { z } from "zod";

// -----------------------------------------------------------------------------
// Consent (Loi 25)
// -----------------------------------------------------------------------------

export const PublicIntakeConsentSchema = z.object({
  accepted: z.literal(true),
  timestamp: z.string().datetime(),
});
export type PublicIntakeConsent = z.infer<typeof PublicIntakeConsentSchema>;

// -----------------------------------------------------------------------------
// Body fields (parsés depuis le multipart par le handler)
// -----------------------------------------------------------------------------
//
// NB : Hono multipart livre tout en string. Le handler parse manuellement
// le JSON pour `consent` (champ à part), puis applique ce schéma à l'agrégat.
// Les fichiers (clé `files` ou `files[]`) sont traités hors de ce schéma.
//
// `comment` ≤ 500 chars (cadrage §2 surface utilisateur).
// `email` optionnel mais validé format si présent.
// `website` est le honeypot — si non-vide → reject (handler).

export const PublicIntakePostBodySchema = z.object({
  /** JSON consent (Loi 25 §3.11). Obligatoire. */
  consent: PublicIntakeConsentSchema,
  /** Commentaire libre client final, ≤ 500 chars. Optionnel. */
  comment: z.string().max(500).optional(),
  /** Email contact optionnel, format validé. */
  email: z.string().email().optional(),
  /**
   * Honeypot field — DOIT rester vide. Si non-vide → bot détecté côté
   * handler → réponse anti-leak unique. Le schéma laisse passer (pas de
   * validation Zod) car on veut que le handler décide du log + reject
   * pas Zod générique 400 (parité décision PO B2 9.N.1.B2).
   */
  website: z.string().optional(),
});
export type PublicIntakePostBody = z.infer<typeof PublicIntakePostBodySchema>;

// -----------------------------------------------------------------------------
// Response 201 — succès POST
// -----------------------------------------------------------------------------
//
// Wire shape de succès : `intake_event_id` opaque retourné au client final.
// Le client final peut afficher un message de confirmation avec ce ref ID
// (ex: « Soumission reçue, référence ABC-123 »). Le broker, lui, voit la
// soumission via le sous-onglet 9.N.3.
//
// Anti-leak : pas de `tenant_id`, pas de `project_id`, pas de
// `submission_count`, pas de `intake_link_id` projeté ici. Seul l'UUID
// `intake_event_id` (opaque) sort.

export const PublicIntakePostSuccessSchema = z.object({
  status: z.literal("received"),
  intake_event_id: z.string().uuid(),
});
export type PublicIntakePostSuccess = z.infer<typeof PublicIntakePostSuccessSchema>;

// -----------------------------------------------------------------------------
// 9.U.5 — magic-link public VERSIONNÉ (data-only, JSON).
// -----------------------------------------------------------------------------

/**
 * Métadonnée publique d'un lien VERSIONNÉ (GET). Expose UNIQUEMENT `kind`,
 * `language` et le `schema` public exact de la version — AUCUN titre de
 * formulaire, nom de projet, tenant ni métadonnée interne (anti-leak, décision
 * PO 2026-08-01). Le FE dispatch sur `kind==="form"` (legacy = pas de `kind`).
 * `schema` est le contrat JSONB de la version (sections/champs) rendu par
 * `FormRenderer` — typé permissif au wire (le FE le caste en `RenderSchema`).
 */
export const PublicVersionedIntakeMetadataSchema = z.object({
  kind: z.literal("form"),
  language: z.enum(["fr", "en"]),
  schema: z.record(z.unknown()),
});
export type PublicVersionedIntakeMetadata = z.infer<typeof PublicVersionedIntakeMetadataSchema>;

/**
 * Corps du POST public VERSIONNÉ (JSON). `.strict()` — aucun champ serveur
 * accepté du wire. `submission_id` = clé d'idempotence générée client (UUID),
 * scellée par lien via l'index unique partiel (migration 0057). Le consentement
 * Loi 25 est SÉPARÉ des données du formulaire (timing server-authoritative).
 * `data` = valeurs de champs uniquement (validées serveur contre la version
 * exacte via `buildFormResponseDataSchema(schema,{mode:"submit"})`).
 */
export const PublicVersionedIntakeSubmitSchema = z
  .object({
    data: z.record(z.unknown()),
    consent: PublicIntakeConsentSchema,
    submission_id: z.string().uuid(),
    language: z.enum(["fr", "en"]).optional(),
  })
  .strict();
export type PublicVersionedIntakeSubmit = z.infer<typeof PublicVersionedIntakeSubmitSchema>;
